/**
 * Branch Slug Mapping Utility
 *
 * Maps German branch/industry slugs to their localized equivalents in English.
 * Used for routing and URL generation for VP-Enterprise branch pages.
 */

// Mapping of German slugs to localized slugs for each language
export const branchSlugMapping: Record<string, Record<string, string>> = {
    "treuhand-gesellschaft": {
        de: "treuhand-gesellschaft",
        en: "fiduciary-company",
    },
    "treuhand-service": {
        de: "treuhand-service",
        en: "fiduciary-service",
    },
    reinigungsunternehmen: {
        de: "reinigungsunternehmen",
        en: "cleaning-company",
    },
    gebaeudetechnik: {
        de: "gebaeudetechnik",
        en: "building-technology",
    },
    "facility-services": {
        de: "facility-services",
        en: "facility-services",
    },
};

/**
 * Get the localized slug for a given German slug and language
 * @param germanSlug The German slug (e.g., "reinigungsunternehmen")
 * @param locale The target locale ("de" or "en")
 * @returns The localized slug, or the original if no mapping exists
 */
export const getLocalizedBranchSlug = (germanSlug: string, locale: string): string => {
    const mapping = branchSlugMapping[germanSlug];
    if (!mapping) {
        console.warn(`No slug mapping found for branch: ${germanSlug}`);
        return germanSlug;
    }

    return mapping[locale] || germanSlug;
};

/**
 * Get the German slug from a localized slug
 * @param localizedSlug The localized slug (e.g., "services-immobiliers")
 * @param locale The source locale ("de" or "en")
 * @returns The German slug, or the original if no mapping exists
 */
export const getGermanSlugFromLocalized = (localizedSlug: string, locale: string): string => {
    for (const [germanSlug, mappings] of Object.entries(branchSlugMapping)) {
        if (mappings[locale] === localizedSlug) {
            return germanSlug;
        }
    }

    // If no mapping found, assume it's already a German slug
    return localizedSlug;
};

/**
 * Get all German branch slugs
 * @returns Array of German slugs
 */
export const getAllGermanBranchSlugs = (): string[] => {
    return Object.keys(branchSlugMapping);
};

/**
 * Get all localized slugs for a specific language
 * @param locale The target locale ("de" or "en")
 * @returns Array of localized slugs
 */
export const getAllLocalizedBranchSlugs = (locale: string): string[] => {
    return Object.values(branchSlugMapping).map(mapping => mapping[locale] || mapping.de);
};
